/**
 * StepDrillConfiguration - Wizard step for drill requirements configuration
 * Shows auto-generated drill requirements based on vessel configuration
 * Allows editing frequencies and adding custom drills with explicit save
 */

import React, { useMemo, useState, useEffect } from 'react';
import { 
  AlertCircle, 
  Info, 
  Settings, 
  Plus, 
  Shield,
  Package,
  Calendar,
  CheckCircle,
  ChevronRight,
  Trash2,
  Edit2,
  Save,
  X
} from 'lucide-react';
import { useDrillRegister } from '../../../../../hooks/domain/useDrillRegister';
import { getDrillRequirements } from '../../../../../config/drillRequirements';

const StepDrillConfiguration = ({ vesselConfig, onChange }) => {
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [editingDrill, setEditingDrill] = useState(null);
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [customDrillForm, setCustomDrillForm] = useState({
    name: '',
    frequencyDays: 90,
    complianceLevel: 'Recommended',
    regulatorySource: 'Company SMS',
    note: ''
  });
  
  // Get drill register and update functions
  const { 
    drillRegister, 
    getDrillRegister,
    updateDrillFrequency,
    addCustomDrill,
    archiveDrill 
  } = useDrillRegister();
  
  // Local state for edits (not saved until user saves settings)
  const [localDrills, setLocalDrills] = useState([]);
  const [pendingChanges, setPendingChanges] = useState({
    frequencyUpdates: {}, // { drillType: newFrequency }
    newDrills: [],        // Array of new custom drills to add
    archivedDrills: []    // Array of drill IDs to archive
  });
  
  // Initialize local drills from register - only on mount
  useEffect(() => {
    const register = getDrillRegister();
    if (register && register.length > 0) {
      setLocalDrills(register);
    } else {
      // Fallback to generating from vessel config
      const requirements = getDrillRequirements(vesselConfig);
      const generatedDrills = Object.entries(requirements).map(([drillType, req]) => ({
        drillType,
        name: req.name || drillType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) + ' Drill',
        frequencyDays: req.frequency_days,
        complianceLevel: req.compliance_level,
        regulatorySource: req.regulatory_source,
        note: req.note,
        configurable: req.configurable !== false,
        warning: req.warning
      }));
      setLocalDrills(generatedDrills);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally only run on mount to avoid render loops
  
  // Handle frequency change (local edit, not saved yet)
  const handleFrequencyChange = (drillType, newFrequency) => {
    // Update local display
    setLocalDrills(prev => prev.map(drill => 
      drill.drillType === drillType 
        ? { ...drill, frequencyDays: parseInt(newFrequency) }
        : drill
    ));
    
    // Track the change for later save
    setPendingChanges(prev => ({
      ...prev,
      frequencyUpdates: {
        ...prev.frequencyUpdates,
        [drillType]: parseInt(newFrequency)
      }
    }));
    
    // Mark settings as having unsaved changes
    onChange({
      ...vesselConfig,
      drillConfigPending: true
    });
  };
  
  // Handle adding custom drill (local, not saved yet)
  const handleAddCustomDrill = () => {
    if (!customDrillForm.name.trim()) {
      alert('Please enter a drill name');
      return;
    }
    
    const newDrill = {
      ...customDrillForm,
      drillType: `custom_${Date.now()}`,
      id: `temp_${Date.now()}`, // Temporary ID until saved
      isNew: true
    };
    
    // Add to local display
    setLocalDrills(prev => [...prev, newDrill]);
    
    // Track for save
    setPendingChanges(prev => ({
      ...prev,
      newDrills: [...prev.newDrills, newDrill]
    }));
    
    // Reset form and close
    setCustomDrillForm({
      name: '',
      frequencyDays: 90,
      complianceLevel: 'Recommended',
      regulatorySource: 'Company SMS',
      note: ''
    });
    setShowAddCustom(false);
    
    // Mark as having changes
    onChange({
      ...vesselConfig,
      drillConfigPending: true
    });
  };
  
  // Handle removing drill (soft delete/archive)
  const handleRemoveDrill = (drillId) => {
    // Remove from local display
    setLocalDrills(prev => prev.filter(d => d.id !== drillId));
    
    // Track for archive on save
    setPendingChanges(prev => ({
      ...prev,
      archivedDrills: [...prev.archivedDrills, drillId]
    }));
    
    // Mark as having changes
    onChange({
      ...vesselConfig,
      drillConfigPending: true
    });
  };
  
  // Apply pending changes when settings are saved
  useEffect(() => {
    if (vesselConfig.saveDrillChanges) {
      // Apply frequency updates
      Object.entries(pendingChanges.frequencyUpdates).forEach(([drillType, frequency]) => {
        updateDrillFrequency(drillType, frequency);
      });
      
      // Add new custom drills
      pendingChanges.newDrills.forEach(drill => {
        addCustomDrill({
          name: drill.name,
          frequencyDays: drill.frequencyDays,
          complianceLevel: drill.complianceLevel,
          regulatorySource: drill.regulatorySource,
          note: drill.note
        });
      });
      
      // Archive removed drills
      pendingChanges.archivedDrills.forEach(drillId => {
        if (!drillId.startsWith('temp_')) { // Don't archive temporary IDs
          archiveDrill(drillId);
        }
      });
      
      // Clear pending changes
      setPendingChanges({
        frequencyUpdates: {},
        newDrills: [],
        archivedDrills: []
      });
      
      // Reset the save flag
      onChange({
        ...vesselConfig,
        saveDrillChanges: false,
        drillConfigPending: false
      });
    }
  }, [vesselConfig.saveDrillChanges, pendingChanges, updateDrillFrequency, addCustomDrill, archiveDrill]);
  
  // Separate drills by compliance level
  const requiredDrills = localDrills.filter(d => 
    d.complianceLevel === 'Required' || d.compliance_level === 'Required'
  );
  const recommendedDrills = localDrills.filter(d => 
    d.complianceLevel === 'Recommended' || d.compliance_level === 'Recommended'
  );
  const customDrills = localDrills.filter(d => 
    d.isNew || (d.drillType && d.drillType.startsWith('custom_'))
  );
  
  // Get frequency display
  const getFrequencyDisplay = (days) => {
    if (!days) return 'As required';
    if (days === 30) return 'Monthly';
    if (days === 60) return 'Bi-monthly';
    if (days === 90) return 'Quarterly';
    if (days === 180) return '6 Monthly';
    if (days === 365) return 'Annual';
    return `Every ${days} days`;
  };
  
  // Get compliance badge
  const getComplianceBadge = (level) => {
    const badgeClasses = {
      'Required': 'bg-red-100 text-red-800',
      'Recommended': 'bg-blue-100 text-blue-800',
      'Optional': 'bg-gray-100 text-gray-800'
    };
    
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badgeClasses[level] || badgeClasses['Optional']}`}>
        {level}
      </span>
    );
  };
  
  // Handle opening drill settings modal
  const handleOpenSettings = () => {
    // This would open the DrillSettingsModal
    // Since we can't import modals directly in wizard steps, we emit an event
    window.dispatchEvent(new CustomEvent('openDrillSettings'));
  };
  
  // Handle adding company drills
  const handleAddCompanyDrills = () => {
    const currentCompanyDrills = vesselConfig.companyCustomDrills || [];
    // This would typically open a modal to add custom drills
    // For now, we'll update the config directly
    onChange({
      ...vesselConfig,
      companyCustomDrills: currentCompanyDrills
    });
  };

  const hasPendingChanges = Object.keys(pendingChanges.frequencyUpdates).length > 0 ||
                            pendingChanges.newDrills.length > 0 ||
                            pendingChanges.archivedDrills.length > 0;
  
  return (
    <div className="space-y-6">
      {/* Pending Changes Notification */}
      {hasPendingChanges && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-amber-900">Unsaved Changes</h3>
              <p className="mt-1 text-sm text-amber-700">
                You have unsaved drill configuration changes. Click "Save Settings" at the bottom of the page to apply them.
              </p>
              <ul className="mt-2 text-sm text-amber-700 list-disc list-inside">
                {Object.keys(pendingChanges.frequencyUpdates).length > 0 && (
                  <li>{Object.keys(pendingChanges.frequencyUpdates).length} drill frequency changes</li>
                )}
                {pendingChanges.newDrills.length > 0 && (
                  <li>{pendingChanges.newDrills.length} new drills to add</li>
                )}
                {pendingChanges.archivedDrills.length > 0 && (
                  <li>{pendingChanges.archivedDrills.length} drills to remove</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-blue-900">Automatic Drill Requirements</h3>
            <p className="mt-1 text-sm text-blue-700">
              Based on your vessel configuration, the following drills are required or recommended. 
              These requirements are determined by your vessel type, equipment, and regulatory framework.
            </p>
          </div>
        </div>
      </div>
      
      {/* Required Drills Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Mandatory Drills</h3>
        <p className="text-sm text-gray-600">These drills are legally required for your vessel</p>
        
        <div className="space-y-3">
          {requiredDrills.map((drill) => (
            <div key={drill.drillType} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-medium text-gray-900">
                      {drill.name}
                    </h4>
                    {getComplianceBadge('Required')}
                  </div>
                  
                  <div className="mt-2 space-y-1">
                    {/* Frequency */}
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="h-4 w-4" />
                      {drill.configurable !== false ? (
                        <div className="flex items-center gap-2">
                          <span>Frequency:</span>
                          <select
                            value={drill.frequencyDays}
                            onChange={(e) => handleFrequencyChange(drill.drillType, e.target.value)}
                            className="text-sm border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                          >
                            <option value="30">Monthly</option>
                            <option value="60">Bi-monthly</option>
                            <option value="90">Quarterly</option>
                            <option value="180">6 Monthly</option>
                            <option value="365">Annual</option>
                          </select>
                          {pendingChanges.frequencyUpdates[drill.drillType] && (
                            <span className="text-xs text-amber-600 font-medium">Modified</span>
                          )}
                        </div>
                      ) : (
                        <span>Frequency: {getFrequencyDisplay(drill.frequencyDays)} (Required)</span>
                      )}
                    </div>
                    
                    {/* Regulatory Source */}
                    {drill.regulatorySource && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Shield className="h-4 w-4" />
                        <span>{drill.regulatorySource}</span>
                      </div>
                    )}
                    
                    {/* Equipment Dependencies */}
                    {drill.equipmentDescription && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Package className="h-4 w-4" />
                        <span>Requires: {drill.equipmentDescription}</span>
                      </div>
                    )}
                    
                    {/* Warning for non-configurable */}
                    {drill.warning && (
                      <div className="flex items-start gap-2 text-sm text-red-600 mt-2">
                        <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                        <span>{drill.warning}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Status indicator */}
                <div className="flex-shrink-0 ml-4">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Recommended Drills Section */}
      {recommendedDrills.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Recommended Drills</h3>
          <p className="text-sm text-gray-600">Based on your vessel configuration and best practices</p>
          
          <div className="space-y-3">
            {recommendedDrills.map((drill) => (
              <div key={drill.drillType} className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-medium text-gray-900">
                        {drill.name}
                      </h4>
                      {getComplianceBadge('Recommended')}
                    </div>
                    
                    <div className="mt-2 space-y-1">
                      {/* Frequency */}
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="h-4 w-4" />
                        <div className="flex items-center gap-2">
                          <span>Frequency:</span>
                          <select
                            value={drill.frequencyDays}
                            onChange={(e) => handleFrequencyChange(drill.drillType, e.target.value)}
                            className="text-sm border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                          >
                            <option value="30">Monthly</option>
                            <option value="60">Bi-monthly</option>
                            <option value="90">Quarterly</option>
                            <option value="180">6 Monthly</option>
                            <option value="365">Annual</option>
                          </select>
                          {pendingChanges.frequencyUpdates[drill.drillType] && (
                            <span className="text-xs text-amber-600 font-medium">Modified</span>
                          )}
                        </div>
                      </div>
                      
                      {/* Regulatory Source */}
                      {drill.regulatorySource && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Shield className="h-4 w-4" />
                          <span>{drill.regulatorySource}</span>
                        </div>
                      )}
                      
                      {/* Equipment Dependencies */}
                      {drill.equipmentDescription && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Package className="h-4 w-4" />
                          <span>Triggered by: {drill.equipmentDescription}</span>
                        </div>
                      )}
                      
                      {/* Note */}
                      {drill.note && (
                        <div className="text-sm text-gray-500 italic mt-1">
                          {drill.note}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Status indicator */}
                  <div className="flex-shrink-0 ml-4 flex items-center gap-2">
                    <button
                      onClick={() => handleRemoveDrill(drill.id || drill.drillType)}
                      className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      title="Remove this drill"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                    <Info className="h-5 w-5 text-blue-500" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Custom Drills Section */}
      {(customDrills.length > 0 || showAddCustom) && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Custom Drills</h3>
          <p className="text-sm text-gray-600">Company-specific drills added to your SMS</p>
          
          <div className="space-y-3">
            {customDrills.map((drill) => (
              <div key={drill.drillType} className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-medium text-gray-900">
                        {drill.name}
                      </h4>
                      {getComplianceBadge(drill.complianceLevel || 'Required')}
                      <span className="text-xs text-purple-600 font-medium">Custom</span>
                    </div>
                    
                    <div className="mt-2 space-y-1">
                      {/* Frequency */}
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="h-4 w-4" />
                        <div className="flex items-center gap-2">
                          <span>Frequency:</span>
                          <select
                            value={drill.frequencyDays}
                            onChange={(e) => handleFrequencyChange(drill.drillType, e.target.value)}
                            className="text-sm border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                          >
                            <option value="30">Monthly</option>
                            <option value="60">Bi-monthly</option>
                            <option value="90">Quarterly</option>
                            <option value="180">6 Monthly</option>
                            <option value="365">Annual</option>
                          </select>
                          {pendingChanges.frequencyUpdates[drill.drillType] && (
                            <span className="text-xs text-amber-600 font-medium">Modified</span>
                          )}
                        </div>
                      </div>
                      
                      {/* Note */}
                      {drill.note && (
                        <div className="text-sm text-gray-500 italic mt-1">
                          {drill.note}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Action buttons */}
                  <div className="flex-shrink-0 ml-4 flex items-center gap-2">
                    <button
                      onClick={() => handleRemoveDrill(drill.id || drill.drillType)}
                      className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      title="Remove this drill"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                    <Shield className="h-5 w-5 text-purple-500" />
                  </div>
                </div>
              </div>
            ))}
            
            {/* Add Custom Drill Form */}
            {showAddCustom && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Add Custom Drill</h4>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Drill Name</label>
                    <input
                      type="text"
                      value={customDrillForm.name}
                      onChange={(e) => setCustomDrillForm(prev => ({ ...prev, name: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      placeholder="e.g., Emergency Towing Drill"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Frequency</label>
                      <select
                        value={customDrillForm.frequencyDays}
                        onChange={(e) => setCustomDrillForm(prev => ({ ...prev, frequencyDays: parseInt(e.target.value) }))}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      >
                        <option value="30">Monthly</option>
                        <option value="60">Bi-monthly</option>
                        <option value="90">Quarterly</option>
                        <option value="180">6 Monthly</option>
                        <option value="365">Annual</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Compliance Level</label>
                      <select
                        value={customDrillForm.complianceLevel}
                        onChange={(e) => setCustomDrillForm(prev => ({ ...prev, complianceLevel: e.target.value }))}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      >
                        <option value="Required">Required</option>
                        <option value="Recommended">Recommended</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Notes (Optional)</label>
                    <textarea
                      value={customDrillForm.note}
                      onChange={(e) => setCustomDrillForm(prev => ({ ...prev, note: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      rows={2}
                      placeholder="Additional notes about this drill..."
                    />
                  </div>
                  
                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      onClick={() => {
                        setShowAddCustom(false);
                        setCustomDrillForm({
                          name: '',
                          frequencyDays: 90,
                          complianceLevel: 'Recommended',
                          regulatorySource: 'Company SMS',
                          note: ''
                        });
                      }}
                      className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddCustomDrill}
                      className="px-3 py-1.5 border border-transparent rounded-md text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700"
                    >
                      Add Drill
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Actions Section */}
      <div className="border-t border-gray-200 pt-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Customize Frequencies Button */}
          <button
            onClick={handleOpenSettings}
            className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
          >
            <Settings className="h-4 w-4 mr-2" />
            Customize Drill Frequencies
          </button>
          
          {/* Add Company Drills Button */}
          <button
            onClick={() => setShowAddCustom(true)}
            className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Custom Drill
          </button>
        </div>
        
        {/* Link to full configuration */}
        <div className="mt-4 text-center">
          <button
            onClick={handleOpenSettings}
            className="text-sm text-cyan-600 hover:text-cyan-500 inline-flex items-center"
          >
            Open full drill configuration for detailed settings
            <ChevronRight className="h-4 w-4 ml-1" />
          </button>
        </div>
      </div>
      
      {/* Summary Stats */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-2xl font-semibold text-gray-900">{requiredDrills.length}</p>
            <p className="text-sm text-gray-600">Required Drills</p>
          </div>
          <div>
            <p className="text-2xl font-semibold text-gray-900">{recommendedDrills.length}</p>
            <p className="text-sm text-gray-600">Recommended</p>
          </div>
          <div>
            <p className="text-2xl font-semibold text-gray-900">
              {customDrills.length}
            </p>
            <p className="text-sm text-gray-600">Custom Drills</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepDrillConfiguration;